import React, { forwardRef, createContext, useContext } from 'react';
import classNames from 'classnames';

interface RadioGroupContextValue {
  name?: string;
  value?: string;
  onChange?: (value: string, e: React.ChangeEvent<HTMLInputElement>) => void;
  disabled?: boolean;
}

const RadioGroupContext = createContext<RadioGroupContextValue | null>(null);

export interface RadioProps extends Omit<React.InputHTMLAttributes<HTMLInputElement>, 'type' | 'onChange'> {
  checked?: boolean;
  defaultChecked?: boolean;
  value?: string;
  onChange?: (checked: boolean, e: React.ChangeEvent<HTMLInputElement>) => void;
  labelRef?: React.Ref<HTMLLabelElement>;
}

const Radio = forwardRef<HTMLInputElement, RadioProps>(
  (
    {
      className,
      children,
      checked,
      defaultChecked,
      value,
      disabled,
      onChange,
      name,
      labelRef,
      id,
      ...rest
    },
    ref
  ) => {
    const groupContext = useContext(RadioGroupContext);
    const inputId = id || `radio-${Math.random().toString(36).substr(2, 9)}`;

    const isDisabled = disabled ?? groupContext?.disabled;
    const inputName = name ?? groupContext?.name;
    const isChecked = groupContext ? groupContext.value === value : checked;

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      if (groupContext?.onChange && value !== undefined) {
        groupContext.onChange(value, e);
      }
      onChange?.(e.target.checked, e);
    };

    return (
      <label
        ref={labelRef}
        htmlFor={inputId}
        className={classNames('ecme-radio-wrapper', { disabled: isDisabled }, className)}
      >
        <span className="ecme-radio">
          <input
            ref={ref}
            id={inputId}
            type="radio"
            className="ecme-radio-input"
            name={inputName}
            checked={isChecked}
            defaultChecked={groupContext ? undefined : defaultChecked}
            disabled={isDisabled}
            value={value}
            onChange={handleChange}
            {...rest}
          />
          <span className="ecme-radio-inner" />
        </span>
        {children && <span className="ecme-radio-label">{children}</span>}
      </label>
    );
  }
);

Radio.displayName = 'Radio';

export interface RadioGroupProps {
  name?: string;
  value?: string;
  defaultValue?: string;
  onChange?: (value: string, e: React.ChangeEvent<HTMLInputElement>) => void;
  disabled?: boolean;
  vertical?: boolean;
  className?: string;
  children?: React.ReactNode;
}

export const RadioGroup: React.FC<RadioGroupProps> = ({
  name,
  value: controlledValue,
  defaultValue,
  onChange,
  disabled,
  vertical = false,
  className,
  children,
}) => {
  const [internalValue, setInternalValue] = React.useState(defaultValue);
  const value = controlledValue !== undefined ? controlledValue : internalValue;

  const handleChange = (val: string, e: React.ChangeEvent<HTMLInputElement>) => {
    if (controlledValue === undefined) {
      setInternalValue(val);
    }
    onChange?.(val, e);
  };

  return (
    <RadioGroupContext.Provider value={{ name, value, onChange: handleChange, disabled }}>
      <div className={classNames('ecme-radio-group', { 'flex-col': vertical }, className)}>
        {children}
      </div>
    </RadioGroupContext.Provider>
  );
};

export default Radio;
